/**
 * ConfirmDialog Component
 * Simple confirmation dialog for user actions
 */

import { useEffect, useRef } from 'react';
import { announceToScreenReader, trapFocus } from '../../utils/accessibility';

/**
 * Props for ConfirmDialog component
 */
export interface ConfirmDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Dialog title */
  title: string;
  /** Dialog message */
  message: string;
  /** Confirm button text */
  confirmText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Callback when user confirms */
  onConfirm: () => void;
  /** Callback when user cancels */
  onCancel: () => void;
}

/**
 * ConfirmDialog component
 * Renders a modal confirmation dialog
 */
export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Handle escape key to cancel, announce dialog, and trap focus
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when dialog is open
      document.body.style.overflow = 'hidden';
      
      // Announce dialog to screen readers
      announceToScreenReader(`${title}. ${message}`, 'assertive');
      
      // Trap focus within dialog
      if (dialogRef.current) {
        const cleanup = trapFocus(dialogRef.current);
        return () => {
          cleanup();
          document.removeEventListener('keydown', handleEscape);
          document.body.style.overflow = '';
        };
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onCancel, title, message]);

  if (!isOpen) return null;

  return (
    <div
      className="confirm-dialog-overlay"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-message"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001, // Higher than template browser
        padding: '1rem',
      }}
    >
      <div
        ref={dialogRef}
        className="confirm-dialog-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg)',
          border: '4px solid var(--color-cell-border)',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '500px',
          padding: '2rem',
          fontFamily: 'var(--font-family)',
        }}
      >
        <h2
          id="confirm-dialog-title"
          style={{
            margin: '0 0 1rem 0',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: 'var(--color-fg)',
          }}
        >
          {title}
        </h2>
        
        <p
          id="confirm-dialog-message"
          style={{
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            color: 'var(--color-fg)',
            lineHeight: '1.5',
          }}
        >
          {message}
        </p>
        
        <div
          className="confirm-dialog-actions"
          style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onCancel}
            aria-label={`${cancelText} and close dialog`}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'var(--color-surface)',
              border: '4px solid var(--color-cell-border)',
              color: 'var(--color-fg)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontFamily: 'var(--font-family)',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-cell-active)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--color-surface)';
            }}
          >
            {cancelText}
          </button>
          
          <button
            onClick={onConfirm}
            aria-label={`${confirmText} action`}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'var(--color-cell-active)',
              border: '4px solid var(--color-cell-border)',
              color: 'var(--color-fg)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontFamily: 'var(--font-family)',
              fontWeight: 'bold',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
